import { useEffect, useState, useMemo, useCallback } from 'react';
import api from '../utils/api';
import Loader from '../components/Loader';
import SearchableSelect from '../components/SearchableSelect';
import { formatDateTime } from '../utils/date';
import { PAGE_SIZE_OPTIONS, buildPageList } from '../utils/pagination';

const TAG_STYLE = {
  create: ['var(--green-dim)', 'var(--green)', 'fa-plus'],
  update: ['var(--sky-dim)', 'var(--sky)', 'fa-pen'],
  delete: ['var(--red-dim)', 'var(--red)', 'fa-trash'],
  transfer: ['var(--sea-dim)', 'var(--sea)', 'fa-right-left'],
  login: ['var(--violet-dim)', 'var(--violet)', 'fa-right-to-bracket'],
  logout: ['var(--chip-bg)', 'var(--slate)', 'fa-right-from-bracket'],
};

const STAT_TILES = [
  { action: '', label: 'Total Events', icon: 'fa-list', color: 'var(--ocean)' },
  { action: 'create', label: 'Created', icon: 'fa-plus', color: 'var(--green)' },
  { action: 'update', label: 'Updated', icon: 'fa-pen', color: 'var(--sky)' },
  { action: 'delete', label: 'Deleted', icon: 'fa-trash', color: 'var(--red)' },
  { action: 'transfer', label: 'Transferred', icon: 'fa-right-left', color: 'var(--sea)' },
];

// Friendly labels for the RBAC module keys (course_master, course_level_master,
// module_master, intake_master, …) so the filter reads like the page names
// instead of raw snake_case DB values. Anything not in this map still shows
// up in the filter — just title-cased from its raw key — so new modules
// don't need a code change to be filterable.
const MODULE_LABELS = {
  course_master: 'Course Master',
  course_level_master: 'Course Level Master',
  module_master: 'Module Master',
  intake_master: 'Intake Master',
  component_master: 'Component Master',
  rbac: 'RBAC / Users',
  auth: 'Authentication',
};

function moduleLabel(key) {
  if (!key) return '—';
  return MODULE_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const SORT_COLUMNS = [
  { key: 'created_at', label: 'Timestamp' },
  { key: 'username', label: 'User' },
  { key: 'module', label: 'Module' },
  { key: 'description', label: 'Description' },
  { key: 'action', label: 'Type' },
];

function compareValues(a, b) {
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' });
}

const CURRENT_YEAR = new Date().getFullYear();

export default function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  // Defaults to the current year rather than "All Years" — the log can span
  // years once the portal's been running a while, and most of the time
  // people want to know what happened recently, not wade through history.
  const [yearFilter, setYearFilter] = useState(CURRENT_YEAR);
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'created_at' ? 'desc' : 'asc');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return <i className="fa-solid fa-sort" style={{ color: 'var(--slate-lt)', marginLeft: '.35rem' }} />;
    return <i className={`fa-solid ${sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down'}`} style={{ color: 'var(--ocean)', marginLeft: '.35rem' }} />;
  };

  const load = useCallback((isRefresh) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    Promise.all([
      api.get('/audit-logs'),
      // Non-critical — if this fails, the user filter just falls back to
      // whoever already shows up in the log instead of blocking the page.
      api.get('/users/active').catch(() => ({ data: { data: [] } })),
    ])
      .then(([logsRes, usersRes]) => {
        setLogs(logsRes.data.data);
        setActiveUsers(usersRes.data.data);
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load audit logs'))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, []);

  useEffect(() => { load(false); }, [load]);
  useEffect(() => { setPage(1); }, [search, actionFilter, userFilter, moduleFilter, yearFilter, pageSize]);

  // Active users from tmis.users (is_active = 1) so every current user is
  // selectable, not just whoever happens to already have a log entry —
  // falls back to usernames seen in the log for anyone missing from that
  // list (e.g. a since-deactivated user's historical entries).
  const users = useMemo(() => {
    const byUsername = new Map(activeUsers.map((u) => [u.username, u.role]));
    logs.forEach((l) => { if (l.username && !byUsername.has(l.username)) byUsername.set(l.username, null); });
    return Array.from(byUsername.entries())
      .map(([username, role]) => ({ username, role }))
      .sort((a, b) => a.username.localeCompare(b.username));
  }, [activeUsers, logs]);
  const modules = useMemo(() => Array.from(new Set(logs.map((l) => l.module).filter(Boolean))).sort((a, b) => moduleLabel(a).localeCompare(moduleLabel(b))), [logs]);
  // Every year with at least one event, plus the current year even if it has
  // none yet, so "this year" is always selectable as soon as the page loads.
  const years = useMemo(() => {
    const set = new Set([CURRENT_YEAR]);
    logs.forEach((l) => { if (l.created_at) set.add(new Date(l.created_at).getFullYear()); });
    return Array.from(set).sort((a, b) => b - a);
  }, [logs]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = logs.filter((l) =>
      (!actionFilter || l.action === actionFilter) &&
      (!userFilter || l.username === userFilter) &&
      (!moduleFilter || l.module === moduleFilter) &&
      (!yearFilter || (l.created_at && new Date(l.created_at).getFullYear() === yearFilter)) &&
      (!term || [l.username, l.description, moduleLabel(l.module), l.action].filter(Boolean).some((v) => String(v).toLowerCase().includes(term)))
    );
    const dir = sortDir === 'asc' ? 1 : -1;
    return list.sort((a, b) => compareValues(a[sortKey], b[sortKey]) * dir);
  }, [logs, search, actionFilter, userFilter, moduleFilter, yearFilter, sortKey, sortDir]);

  const statCounts = useMemo(() => {
    const counts = { '': filtered.length };
    for (const l of filtered) counts[l.action] = (counts[l.action] || 0) + 1;
    return counts;
  }, [filtered]);

  const total = filtered.length;
  const effectivePageSize = pageSize === 'All' ? total || 1 : pageSize;
  const totalPages = Math.max(1, Math.ceil(total / effectivePageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    if (pageSize === 'All') return filtered;
    const start = (currentPage - 1) * effectivePageSize;
    return filtered.slice(start, start + effectivePageSize);
  }, [filtered, currentPage, effectivePageSize, pageSize]);

  const rangeStart = total === 0 ? 0 : (pageSize === 'All' ? 1 : (currentPage - 1) * effectivePageSize + 1);
  const rangeEnd = pageSize === 'All' ? total : Math.min(currentPage * effectivePageSize, total);

  const clearFilters = () => {
    setSearch('');
    setActionFilter('');
    setUserFilter('');
    setModuleFilter('');
    setYearFilter(CURRENT_YEAR);
  };

  const hasActiveFilters = !!(search || actionFilter || userFilter || moduleFilter || yearFilter !== CURRENT_YEAR);

  return (
    <div>
      <div className="sh-ey">AUDIT TRAIL · SYSTEM ACTIVITY</div>
      <div className="sh-ti">System Audit Log</div>
      <div className="sh-de">Live record of create/update/delete/transfer/login/logout events across the portal, pulled from the backend audit log.</div>

      {!loading && !error && (
        <div style={{ fontSize: 'var(--text-xs)', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--slate-lt)', marginBottom: '.6rem' }}>
          {yearFilter ? `${yearFilter}${yearFilter === CURRENT_YEAR ? ' year to date' : ''}` : 'All years'}{(search || actionFilter || userFilter || moduleFilter) ? ' · within current filters' : ''}
        </div>
      )}
      {!loading && !error && (
        <div className="g5" style={{ marginBottom: '1.5rem' }}>
          {STAT_TILES.map((t) => (
            <button
              type="button"
              key={t.action || 'total'}
              className="sc"
              style={{ borderTopColor: t.color, textAlign: 'left', cursor: 'pointer', width: '100%' }}
              onClick={() => setActionFilter(t.action)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '.45rem' }}>
                <div style={{ width: 38, height: 38, borderRadius: 9, background: `color-mix(in srgb, ${t.color} 14%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.color, fontSize: '.95rem' }}>
                  <i className={`fa-solid ${t.icon}`} />
                </div>
                {actionFilter === t.action && <span className="pill p-ocean">Filtered</span>}
              </div>
              <div className="sv" style={{ color: t.color }}>{statCounts[t.action] || 0}</div>
              <div className="sl">{t.label}</div>
            </button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div className="icon-field" style={{ flex: '1 1 220px', minWidth: 180 }}>
          <i className="fa-solid fa-magnifying-glass" />
          <input className="finput" placeholder="Search user, description, module…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <SearchableSelect
          className="finput" style={{ width: 175 }} value={yearFilter} onChange={(val) => setYearFilter(val === '' ? '' : Number(val))}
          placeholder="All Years"
          options={[{ value: '', label: 'All Years' }, ...years.map((y) => ({ value: y, label: y === CURRENT_YEAR ? `${y} (this year)` : String(y) }))]}
        />
        <SearchableSelect
          className="finput" style={{ width: 190 }} value={moduleFilter} onChange={setModuleFilter}
          placeholder="All Modules"
          options={[{ value: '', label: 'All Modules' }, ...modules.map((m) => ({ value: m, label: moduleLabel(m) }))]}
        />
        <SearchableSelect
          className="finput" style={{ width: 165 }} value={actionFilter} onChange={setActionFilter}
          placeholder="All Event Types"
          options={[
            { value: '', label: 'All Event Types' },
            { value: 'create', label: 'Create' },
            { value: 'update', label: 'Update' },
            { value: 'delete', label: 'Delete' },
            { value: 'transfer', label: 'Transfer' },
            { value: 'login', label: 'Login' },
            { value: 'logout', label: 'Logout' },
          ]}
        />
        <SearchableSelect
          className="finput" style={{ width: 200 }} value={userFilter} onChange={setUserFilter}
          placeholder="All Users"
          options={[{ value: '', label: 'All Users' }, ...users.map((u) => ({ value: u.username, label: u.role ? `${u.username} (${u.role})` : u.username }))]}
        />
        {hasActiveFilters && (
          <button type="button" className="btn btn-outline btn-toolbar" onClick={clearFilters}><i className="fa-solid fa-arrow-rotate-left" /> Clear</button>
        )}
        <button type="button" className="btn btn-outline btn-toolbar" style={{ marginLeft: 'auto' }} disabled={refreshing} onClick={() => load(true)}>
          <i className={`fa-solid fa-arrows-rotate ${refreshing ? 'fa-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading && <Loader label="Loading audit log…" />}
      {error && !loading && <div className="alert"><i className="fa-solid fa-triangle-exclamation" /><div className="alert-t">{error}</div></div>}

      {!loading && !error && !logs.length && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
          <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: '2rem', color: 'var(--slate-lt)', marginBottom: '.75rem', display: 'block' }} />
          <div style={{ fontWeight: 700, color: 'var(--ink)', marginBottom: '.35rem' }}>No audit events yet</div>
          <div style={{ color: 'var(--slate)' }}>Actions across the portal will appear here as they happen.</div>
        </div>
      )}

      {!loading && !error && !!logs.length && !total && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1rem', color: 'var(--slate)' }}>
          No audit events match the current search/filters.
        </div>
      )}

      {!loading && !error && !!total && (
        <div className="card view-fade">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  {SORT_COLUMNS.map(({ key, label }) => (
                    <th key={key} style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(key)}>
                      {label}{sortIcon(key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((a) => {
                  const [bg, col, icon] = TAG_STYLE[a.action] || TAG_STYLE.update;
                  return (
                    <tr key={a.id}>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: '.75rem', color: 'var(--slate)', whiteSpace: 'nowrap' }}>{formatDateTime(a.created_at)}</td>
                      <td style={{ fontWeight: 600, fontSize: 'var(--text-sm)', whiteSpace: 'nowrap' }}>{a.username || '—'}</td>
                      <td><span title={a.module} style={{ fontFamily: 'var(--font-mono)', fontSize: '.75rem', background: 'var(--cloud)', color: 'var(--ocean)', padding: '.1rem .45rem', borderRadius: 4, fontWeight: 600 }}>{moduleLabel(a.module)}{a.record_id ? ` #${a.record_id}` : ''}</span></td>
                      <td style={{ fontSize: 'var(--text-sm)' }}>{a.description || '—'}</td>
                      <td><span className="pill" style={{ background: bg, color: col, whiteSpace: 'nowrap' }}><i className={`fa-solid ${icon}`} /> {a.action}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginTop: '1.3rem', paddingTop: '1rem', borderTop: '1px solid var(--border-soft)' }}>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--slate)' }}>
              Showing {rangeStart}–{rangeEnd} of {total}
            </div>
            <div className="icon-field" style={{ marginLeft: 'auto' }}>
              <SearchableSelect
                className="finput" style={{ width: 110 }} value={pageSize}
                onChange={(val) => setPageSize(val === 'All' ? 'All' : Number(val))}
                options={PAGE_SIZE_OPTIONS.map((opt) => ({ value: opt, label: String(opt) }))}
              />
            </div>
            {pageSize !== 'All' && totalPages > 1 && (
              <div style={{ display: 'flex', gap: '.3rem', alignItems: 'center' }}>
                <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === 1} onClick={() => setPage(1)}><i className="fa-solid fa-angles-left" /></button>
                <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === 1} onClick={() => setPage((p) => p - 1)}><i className="fa-solid fa-angle-left" /></button>
                {buildPageList(currentPage, totalPages).map((p, idx) => (
                  p === '…'
                    ? <span key={`e${idx}`} style={{ padding: '0 .3rem', color: 'var(--slate-lt)' }}>…</span>
                    : (
                      <button
                        key={p}
                        type="button"
                        className={`btn btn-sm ${p === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    )
                ))}
                <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === totalPages} onClick={() => setPage((p) => p + 1)}><i className="fa-solid fa-angle-right" /></button>
                <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === totalPages} onClick={() => setPage(totalPages)}><i className="fa-solid fa-angles-right" /></button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
